import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { 

  CreateTrainingContentDto, 
  UpdateTrainingContentDto,
  CreateTrainingContentWithFileDto,
  UpdateTrainingContentWithFileDto
} from '../types/dtos/training';
import { createTrainingContentService } from '../services/training-content.service';

const TRAINING_CONTENTS_KEY = 'training-contents';

export const useTrainingContents = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string,
  params?: PaginationParams
) => {
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useQuery({
    queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId, params],
    queryFn: () => contentService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!trainingId && !!moduleId && !!topicId
  });
};

export const useTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string,
  contentId: string
) => {
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useQuery({
    queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId, contentId],
    queryFn: () => contentService.findById(contentId).then((res) => res.data),
    enabled: !!organizationId && !!trainingId && !!moduleId && !!topicId && !!contentId
  });
};

export const useCreateTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const queryClient = useQueryClient();
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useMutation({
    mutationFn: (data: CreateTrainingContentDto) => 
      contentService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId] 
      });
    }
  });
};

export const useUploadTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const queryClient = useQueryClient();
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useMutation({
    mutationFn: (data: CreateTrainingContentWithFileDto) => {
      // Build FormData from the DTO
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('name', data.name);
      formData.append('sequence', data.sequence.toString());
      
      if (data.description) formData.append('description', data.description);
      if (data.typeId) formData.append('typeId', data.typeId);
      if (data.durationMinutes) formData.append('durationMinutes', data.durationMinutes.toString());
      if (data.progressContribution !== undefined) {
        formData.append('progressContribution', data.progressContribution.toString());
      }

      return contentService.uploadWithFile(formData).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId] 
      });
    }
  });
};

export const useUpdateTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const queryClient = useQueryClient();
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingContentDto }) =>
      contentService.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId, id] 
      });
    }
  });
};

export const useUpdateWithFileTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const queryClient = useQueryClient();
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingContentWithFileDto }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('name', data.name);
      formData.append('sequence', data.sequence.toString());
      
      if (data.description) formData.append('description', data.description);
      if (data.typeId) formData.append('typeId', data.typeId);
      if (data.durationMinutes) formData.append('durationMinutes', data.durationMinutes.toString());
      if (data.progressContribution !== undefined) {
        formData.append('progressContribution', data.progressContribution.toString());
      }

      return contentService.updateWithFile(id, formData).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId] 
      });
    }
  });
};

export const useDeleteTrainingContent = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => {
  const queryClient = useQueryClient();
  const contentService = createTrainingContentService(organizationId, trainingId, moduleId, topicId);

  return useMutation({
    mutationFn: (id: string) => contentService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_CONTENTS_KEY, organizationId, trainingId, moduleId, topicId] 
      });
    }
  });
};

